<script setup lang="ts">
import type { PropType } from 'vue'
import type { ApiError } from '@/api/errors'
/**
 * ErrorState — full-card error display.
 *
 * Visual sibling of `EmptyState` and `NotFoundCard`. Use when a
 * fetch fails and the page can't render its primary content.
 * For "not found" cases use `NotFoundCard`; for "no results"
 * use `EmptyState`. This component is for *something went wrong*.
 *
 *   <ErrorState
 *     :error="trademarks.error"
 *     @retry="trademarks.retryFetchList"
 *   />
 *
 * Props:
 *   title     headline (defaults to one chosen from the error, translated)
 *   message   sub-line; falls back to `error.message` or generic
 *   error     the typed error (HttpError / AuthError / Network…)
 *             used to choose an appropriate icon + status code
 *   variant   'card' (default) | 'inline' — inline drops the
 *             padding for use inside narrow contexts
 *   showCode  show the HTTP status code if present (default true)
 *
 * Emits:
 *   retry     fired when the user clicks the "Try again" button
 */
import { computed } from 'vue'
import i18n from '@/i18n'

/* The global composer: shared primitives must render without the plugin. */
const { t } = i18n.global

const props = defineProps({
  // Anything thrown: an ApiError subclass most of the time, but a render
  // failure or a plain Error can land here too.
  error: { type: [Object, Error] as PropType<ApiError | Error | null>, default: null },
  title: { type: String, default: '' },
  message: { type: String, default: '' },
  variant: { type: String, default: 'card' }, // 'card' | 'inline'
  showCode: { type: Boolean, default: true },
  /** Empty means the catalogue's "Try again" in the reader's language. */
  retryLabel: { type: String, default: '' },
})

defineEmits(['retry'])

/* Icon + tone selected from the error class. */
const icon = computed(() => {
  const name = props.error?.name
  const status = (props.error as ApiError | null)?.status
  if (name === 'NetworkError') return 'fa-solid fa-wifi'
  if (name === 'TimeoutError') return 'fa-regular fa-clock'
  if (name === 'AuthError') return 'fa-solid fa-lock'
  if (status === 429) return 'fa-solid fa-gauge-high'
  if (status != null && status >= 500) return 'fa-solid fa-server'
  return 'fa-solid fa-triangle-exclamation'
})

const code = computed(() => {
  if (!props.showCode) return ''
  const s = (props.error as ApiError | null)?.status
  return typeof s === 'number' ? String(s) : ''
})

const headline = computed(() => {
  if (props.title) return props.title
  const name = props.error?.name
  const status = (props.error as ApiError | null)?.status
  if (name === 'NetworkError') return t('ui.error.offlineTitle')
  if (name === 'TimeoutError') return t('ui.error.timeoutTitle')
  if (name === 'AuthError') return t('ui.error.authTitle')
  if (status === 429) return t('ui.error.rateTitle')
  if ((status ?? 0) >= 500) return t('ui.error.serverTitle')
  return t('states.errorTitle')
})

/* No "contact support" here: the product has no support channel to point at,
 * and a sentence sending a lawyer to one that does not exist is worse than
 * none. */
const body = computed(() => {
  if (props.message) return props.message
  const name = props.error?.name
  if (name === 'NetworkError') return t('ui.error.offline')
  if (name === 'TimeoutError') return t('ui.error.timeout')
  if (name === 'AuthError') return t('ui.error.auth')
  if ((props.error as ApiError | null)?.status === 429) return t('ui.error.rate')
  return props.error?.message || t('ui.error.generic')
})
</script>

<template>
  <article :class="['error-state', `error-state--${variant}`]" role="alert">
    <span class="error-state__icon" aria-hidden="true">
      <i :class="icon"></i>
    </span>
    <h2 class="error-state__title">
      {{ headline }}
      <span v-if="code" class="error-state__code">{{ code }}</span>
    </h2>
    <p class="error-state__sub">{{ body }}</p>
    <div class="error-state__actions">
      <button class="btn btn--primary" type="button" @click="$emit('retry')">
        <i class="fa-solid fa-rotate"></i>
        {{ retryLabel || t('common.retry') }}
      </button>
      <slot name="actions" />
    </div>
  </article>
</template>

<style scoped>
.error-state {
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-3);
}
.error-state--card {
  padding: var(--space-8) var(--space-5);
}
.error-state--inline {
  padding: var(--space-4);
  flex-direction: row;
  text-align: left;
  align-items: center;
  gap: var(--space-4);
}
.error-state__icon {
  display: inline-grid;
  place-items: center;
  width: 56px;
  height: 56px;
  border-radius: var(--radius-full);
  background: var(--color-risk-high-soft);
  color: var(--color-risk-high);
  font-size: 22px;
  flex-shrink: 0;
}
.error-state--inline .error-state__icon {
  width: 40px;
  height: 40px;
  font-size: 16px;
}
.error-state__title {
  font-size: var(--fs-xl);
  font-weight: var(--fw-semibold);
  color: var(--color-text);
  margin: 0;
  letter-spacing: -0.01em;
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
}
.error-state--inline .error-state__title {
  font-size: var(--fs-lg);
}
.error-state__code {
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  font-feature-settings: 'tnum';
  color: var(--color-risk-high);
  background: var(--color-risk-high-soft);
  padding: 2px 8px;
  border-radius: var(--radius-full);
  letter-spacing: 0.04em;
}
.error-state__sub {
  color: var(--color-text-secondary);
  max-width: 480px;
  margin: 0;
  font-size: var(--fs-base);
  line-height: var(--line-loose);
}
.error-state--inline .error-state__sub {
  max-width: none;
}
.error-state__actions {
  display: flex;
  gap: var(--space-2);
  margin-top: var(--space-2);
  flex-wrap: wrap;
  justify-content: center;
}
.error-state--inline .error-state__actions {
  margin-top: 0;
  margin-left: auto;
}

@media (max-width: 640px) {
  .error-state--inline {
    flex-direction: column;
    text-align: center;
  }
  .error-state--inline .error-state__actions {
    margin-left: 0;
  }
}
</style>
